import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import api from '../api/client';

// Hardcoded presets provide richer Spanish UI labels per variable.
// Templates fetched from Meta that aren't here will be auto-generated via buildPresetFromMetaTemplate.
const TEMPLATE_PRESETS = {
  hello_world: {
    bodyVarCount: 0,
    hasHeaderImage: false,
    hasDynamicButton: false,
    language: 'en_US',
    description: 'Plantilla de prueba pre-aprobada (ingles). Sin variables.',
    bodyText: 'Hello! This is a test message from WhatsApp.',
    buttonText: null,
  },
  promo_producto: {
    bodyVarCount: 3,
    bodyLabels: ['Nombre del cliente', 'Nombre del producto', 'Precio (ej: 699)'],
    hasHeaderImage: true,
    hasDynamicButton: true,
    buttonLabel: 'Path del producto (ej: products/bambu-sweater)',
    language: 'es_MX',
    description: 'Promocion de producto con imagen, precio y link al producto.',
    // eslint-disable-next-line no-template-curly-in-string
    bodyText: 'Hola {{1}}, te presentamos {{2}} a solo ${{3}} MXN. No te lo pierdas!',
    buttonText: 'Ver producto',
  },
  carrito_abandonado: {
    bodyVarCount: 1,
    bodyLabels: ['Nombre del cliente'],
    hasHeaderImage: false,
    hasDynamicButton: true,
    buttonLabel: 'Path del checkout (ej: checkouts/abc123)',
    language: 'es_MX',
    description: 'Recordatorio de carrito abandonado.',
    bodyText: 'Hola {{1}}, dejaste productos en tu carrito. Completa tu compra antes de que se agoten!',
    buttonText: 'Completar compra',
  },
  nuevo_producto: {
    bodyVarCount: 3,
    bodyLabels: ['Nombre del cliente', 'Nombre del lanzamiento', 'Descripcion corta'],
    hasHeaderImage: true,
    hasDynamicButton: true,
    buttonLabel: 'Path de la coleccion (ej: collections/nuevo)',
    language: 'es_MX',
    description: 'Anuncio de nuevo producto o coleccion.',
    bodyText: 'Hola {{1}}, acaba de llegar {{2}}. {{3}}. Descubrelo ahora!',
    buttonText: 'Ver coleccion',
  },
  descuento_codigo: {
    bodyVarCount: 6,
    bodyLabels: ['Nombre del cliente', '% de descuento (ej: 25)', 'Aplica a (ej: toda la tienda)', 'Fecha inicio (ej: 15 de abril)', 'Fecha fin (ej: 16 de abril)', 'Código de descuento (ej: WHA25)'],
    hasHeaderImage: true,
    hasDynamicButton: true,
    buttonLabel: 'Path de destino (ej: discount/WHA25)',
    language: 'es_MX',
    description: 'Descuento general por tiempo limitado.',
    bodyText: 'Hola {{1}}, tenemos *{{2}}%* de descuento en *{{3}}*. Valido del *{{4}}* al *{{5}}*. Usa el codigo *{{6}}* al pagar.',
    buttonText: 'Aplicar Descuento',
  },
};

// Convert a Meta API template object into a preset usable by the UI.
function buildPresetFromMetaTemplate(t) {
  const components = t.components || [];
  const header = components.find((c) => c.type === 'HEADER');
  const body = components.find((c) => c.type === 'BODY');
  const buttons = components.find((c) => c.type === 'BUTTONS');
  const urlBtn = buttons?.buttons?.find((b) => b.type === 'URL');

  const bodyText = body?.text || '';
  const varMatches = bodyText.match(/\{\{\s*\d+\s*\}\}/g) || [];
  const bodyVarCount = varMatches.length;
  const hasHeaderImage = header?.format === 'IMAGE';
  const hasDynamicButton = !!urlBtn && /\{\{\s*\d+\s*\}\}/.test(urlBtn.url || '');

  return {
    bodyVarCount,
    bodyLabels: Array.from({ length: bodyVarCount }, (_, i) => `Variable ${i + 1}`),
    hasHeaderImage,
    hasDynamicButton,
    buttonLabel: hasDynamicButton ? 'Valor dinámico de la URL del botón' : null,
    language: t.language || 'es_MX',
    description: `Plantilla de Meta (${t.status || 'APPROVED'}) — ${t.category || 'sin categoría'}`,
    bodyText,
    buttonText: urlBtn?.text || null,
  };
}

// Mexico City is UTC-6 year-round (no DST since 2022).
const CDMX_OFFSET = '-06:00';

// Convert UTC ISO string from DB to "YYYY-MM-DDTHH:mm" for <input type="datetime-local">,
// reading the value as CDMX wall-clock regardless of the browser's timezone.
function utcToCdmxInput(isoUtcStr) {
  if (!isoUtcStr) return '';
  const ms = new Date(isoUtcStr).getTime();
  if (Number.isNaN(ms)) return '';
  const shifted = new Date(ms - 6 * 60 * 60 * 1000);
  return shifted.toISOString().slice(0, 16);
}

// Convert "YYYY-MM-DDTHH:mm" from the input (interpreted as CDMX local) to UTC ISO for the API.
function cdmxInputToUtc(localStr) {
  if (!localStr) return null;
  const seconds = localStr.length === 16 ? ':00' : '';
  const d = new Date(`${localStr}${seconds}${CDMX_OFFSET}`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function resolvePreviewVar(descriptor) {
  if (!descriptor) return '___';
  if (descriptor.type === 'contact_field') {
    const labels = { nombre: 'Juan', telefono: '+5215512345678', email: 'juan@email.com' };
    return labels[descriptor.field] || descriptor.field;
  }
  if (descriptor.type === 'static') return descriptor.value || '___';
  return '___';
}

function WhatsAppPreview({ preset, form }) {
  if (!preset) return null;

  let previewText = preset.bodyText || '';
  (form.template_variables || []).forEach((v, i) => {
    previewText = previewText.replace(`{{${i + 1}}}`, resolvePreviewVar(v));
  });

  return (
    <div style={{ position: 'sticky', top: 20 }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: '#333' }}>Vista previa</div>
      <div style={{
        background: '#e5ddd5',
        backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'400\' height=\'400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cdefs%3E%3Cpattern id=\'p\' width=\'60\' height=\'60\' patternUnits=\'userSpaceOnUse\'%3E%3Cpath d=\'M30 5 L35 15 L30 25 L25 15Z\' fill=\'%23d4ccb5\' opacity=\'0.15\'/%3E%3C/pattern%3E%3C/defs%3E%3Crect fill=\'url(%23p)\' width=\'400\' height=\'400\'/%3E%3C/svg%3E")',
        borderRadius: 12,
        padding: 20,
        minHeight: 300,
        display: 'flex',
        justifyContent: 'flex-end',
        flexDirection: 'column',
      }}>
        <div style={{
          background: '#dcf8c6',
          borderRadius: '8px 0 8px 8px',
          maxWidth: 280,
          alignSelf: 'flex-end',
          boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
          overflow: 'hidden',
        }}>
          {/* Header image */}
          {preset.hasHeaderImage && (
            <div style={{
              width: '100%',
              height: 160,
              background: '#c8c8c8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}>
              {form.media_url ? (
                <img
                  src={form.media_url}
                  alt="Preview"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div style={{ color: '#999', fontSize: 12 }}>Imagen del header</div>
              )}
            </div>
          )}

          {/* Body */}
          <div style={{ padding: '8px 12px 4px' }}>
            <div style={{ fontSize: 13, lineHeight: 1.4, color: '#303030', whiteSpace: 'pre-wrap' }}
              dangerouslySetInnerHTML={{ __html: previewText.replace(/\*([^*]+)\*/g, '<b>$1</b>') }}
            />
            <div style={{ fontSize: 11, color: '#999', textAlign: 'right', marginTop: 4 }}>
              {new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>

          {/* Button */}
          {preset.buttonText && (
            <div style={{
              borderTop: '1px solid #c8e6c0',
              padding: '10px 12px',
              textAlign: 'center',
              fontSize: 13,
              color: '#53bdeb',
              fontWeight: 500,
              cursor: 'default',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}>
              <span style={{ fontSize: 14 }}>&#x1F517;</span>
              {preset.buttonText}
            </div>
          )}
        </div>

        {/* Opt-out footer */}
        <div style={{
          background: '#dcf8c6',
          borderRadius: '8px 0 8px 8px',
          maxWidth: 280,
          alignSelf: 'flex-end',
          boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
          marginTop: 4,
          padding: '6px 12px',
        }}>
          <div style={{ fontSize: 11, color: '#888', fontStyle: 'italic' }}>
            Responde PARAR para no recibir mas mensajes
          </div>
        </div>
      </div>
    </div>
  );
}

// Variable descriptor type: { type: 'static' | 'contact_field', value?: string, field?: string }
function VariableEditor({ label, descriptor, onChange }) {
  const current = descriptor || { type: 'static', value: '' };

  return (
    <div style={{ border: '1px solid #eee', borderRadius: 6, padding: 12, marginBottom: 10, background: '#fafafa' }}>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: '#333' }}>{label}</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <select
          value={current.type}
          onChange={(e) => {
            const type = e.target.value;
            if (type === 'contact_field') onChange({ type: 'contact_field', field: 'nombre' });
            else onChange({ type: 'static', value: current.value || '' });
          }}
          style={{ padding: 8, border: '1px solid #ddd', borderRadius: 4, fontSize: 12 }}
        >
          <option value="static">Valor fijo</option>
          <option value="contact_field">Campo del contacto</option>
        </select>
        {current.type === 'static' ? (
          <input
            value={current.value || ''}
            onChange={(e) => onChange({ type: 'static', value: e.target.value })}
            placeholder="Valor..."
            style={{ flex: 1, padding: 8, border: '1px solid #ddd', borderRadius: 4, fontSize: 12 }}
          />
        ) : (
          <select
            value={current.field || 'nombre'}
            onChange={(e) => onChange({ type: 'contact_field', field: e.target.value })}
            style={{ flex: 1, padding: 8, border: '1px solid #ddd', borderRadius: 4, fontSize: 12 }}
          >
            <option value="nombre">nombre</option>
            <option value="telefono">telefono</option>
            <option value="email">email</option>
          </select>
        )}
      </div>
    </div>
  );
}

export default function CampaignCreate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id: editId } = useParams();
  const isEditing = !!editId;
  const duplicateId = searchParams.get('duplicate');
  const [lists, setLists] = useState([]);
  const [metaTemplates, setMetaTemplates] = useState({}); // { name: preset } from Meta API
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesMsg, setTemplatesMsg] = useState('');
  const [form, setForm] = useState({
    nombre: '',
    tipo: 'promo',
    template_name: '',
    template_language: 'es_MX',
    template_variables: [], // array of descriptors
    button_url_variable: null, // descriptor or null
    mensaje: '',
    media_url: '',
    media_type: 'none',
    link: '',
    contact_list_id: '',
    scheduled_at: '',
    estado: 'borrador',
  });
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const skipTemplateReset = useRef(!!duplicateId || isEditing);

  // Hardcoded presets win (richer Spanish labels). Meta-only templates fill in the rest.
  const mergedPresets = { ...metaTemplates, ...TEMPLATE_PRESETS };
  const preset = mergedPresets[form.template_name];

  const loadTemplates = async () => {
    setTemplatesLoading(true);
    setTemplatesMsg('');
    try {
      const res = await api.get('/whatsapp/templates');
      const map = {};
      (res.data || []).forEach((t) => {
        map[t.name] = buildPresetFromMetaTemplate(t);
      });
      setMetaTemplates(map);
      setTemplatesMsg(`${res.data.length} plantillas cargadas de Meta`);
      setTimeout(() => setTemplatesMsg(''), 3000);
    } catch (err) {
      setTemplatesMsg(err.response?.data?.error || 'Error al cargar plantillas de Meta');
    } finally {
      setTemplatesLoading(false);
    }
  };

  useEffect(() => {
    api.get('/contact-lists').then((res) => setLists(res.data));
    loadTemplates();
    const sourceId = editId || duplicateId;
    if (sourceId) {
      api.get(`/campaigns/${sourceId}`).then((res) => {
        const c = res.data;
        setForm({
          nombre: isEditing ? c.nombre : c.nombre + ' (copia)',
          tipo: c.tipo || 'promo',
          template_name: c.template_name || '',
          template_language: c.template_language || 'es_MX',
          template_variables: c.template_variables || [],
          button_url_variable: c.button_url_variable || null,
          mensaje: c.mensaje || '',
          media_url: c.media_url || '',
          media_type: c.media_type || 'none',
          link: c.link || '',
          contact_list_id: c.contact_list_id ? String(c.contact_list_id) : '',
          scheduled_at: isEditing ? utcToCdmxInput(c.scheduled_at) : '',
          estado: 'borrador',
        });
      }).catch((err) => {
        setError(err.response?.data?.error || 'No se pudo cargar la campaña');
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When template changes, reset variables and media/button settings
  useEffect(() => {
    if (!preset) return;
    if (skipTemplateReset.current) { skipTemplateReset.current = false; return; }
    setForm((f) => ({
      ...f,
      template_language: preset.language,
      template_variables: Array.from({ length: preset.bodyVarCount }, (_, i) =>
        i === 0 ? { type: 'contact_field', field: 'nombre' } : { type: 'static', value: '' }
      ),
      button_url_variable: preset.hasDynamicButton ? { type: 'static', value: '' } : null,
      media_type: preset.hasHeaderImage ? 'image' : 'none',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.template_name]);

  const saveCampaign = async () => {
    const payload = { ...form };
    // Interpret the scheduled_at input as CDMX wall-clock and serialize to UTC for the API.
    payload.scheduled_at = cdmxInputToUtc(form.scheduled_at);
    if (payload.scheduled_at) payload.estado = 'programada';
    if (isEditing) {
      const { data } = await api.put(`/campaigns/${editId}`, payload);
      return data;
    }
    const { data } = await api.post('/campaigns', payload);
    return data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await saveCampaign();
      navigate('/campaigns');
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar campana');
    }
  };

  const handleCreateAndSend = async () => {
    if (!form.nombre || !form.template_name || !form.contact_list_id) {
      setError('Completa nombre, template y lista de contactos');
      return;
    }
    if (preset?.hasHeaderImage && !form.media_url) {
      setError('Esta plantilla requiere una URL de imagen');
      return;
    }
    const verb = isEditing ? 'Guardar la campana y enviarla inmediatamente?' : 'Crear la campana y enviarla inmediatamente?';
    if (!window.confirm(verb)) return;
    setError('');
    setSending(true);
    try {
      const campaign = await saveCampaign();
      await api.post(`/campaigns/${campaign.id}/send`);
      navigate(`/campaigns/${campaign.id}/dashboard`);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar o enviar la campana');
      setSending(false);
    }
  };

  const updateBodyVar = (index, descriptor) => {
    const vars = [...form.template_variables];
    vars[index] = descriptor;
    setForm({ ...form, template_variables: vars });
  };

  const inputStyle = {
    width: '100%', padding: 10, border: '1px solid #ddd', borderRadius: 4,
    marginBottom: 16, boxSizing: 'border-box', fontSize: 13,
  };

  return (
    <div>
      <h1>{isEditing ? 'Editar Campana' : 'Nueva Campana'}</h1>
      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
      <form onSubmit={handleSubmit} style={{ background: '#fff', borderRadius: 8, padding: 30, flex: 1, minWidth: 0 }}>
        {error && <div style={{ background: '#f8d7da', padding: 10, borderRadius: 4, marginBottom: 16, fontSize: 13 }}>{error}</div>}

        <label style={{ fontSize: 13, fontWeight: 600 }}>Nombre de la campana</label>
        <input style={inputStyle} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} required />

        <label style={{ fontSize: 13, fontWeight: 600 }}>Tipo</label>
        <select style={inputStyle} value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
          <option value="promo">Promocion</option>
          <option value="nuevo_producto">Nuevo Producto</option>
          <option value="carrito_abandonado">Carrito Abandonado</option>
        </select>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <label style={{ fontSize: 13, fontWeight: 600 }}>Template de WhatsApp</label>
          <button
            type="button"
            onClick={loadTemplates}
            disabled={templatesLoading}
            title="Volver a leer las plantillas aprobadas desde Meta"
            style={{
              padding: '4px 10px', border: '1px solid #ddd', borderRadius: 4,
              background: '#fff', cursor: templatesLoading ? 'wait' : 'pointer',
              fontSize: 12, color: '#555',
            }}
          >
            {templatesLoading ? 'Cargando...' : '🔄 Refrescar plantillas'}
          </button>
        </div>
        {templatesMsg && (
          <div style={{ fontSize: 11, color: '#666', marginBottom: 6 }}>{templatesMsg}</div>
        )}
        <select style={inputStyle} value={form.template_name} onChange={(e) => setForm({ ...form, template_name: e.target.value })}>
          <option value="">Selecciona un template...</option>
          {Array.from(new Set([...Object.keys(TEMPLATE_PRESETS), ...Object.keys(metaTemplates)])).sort().map((name) => (
            <option key={name} value={name}>
              {name}{!TEMPLATE_PRESETS[name] ? ' (Meta)' : ''}
            </option>
          ))}
        </select>
        {preset && (
          <div style={{ background: '#e8f4fd', padding: 10, borderRadius: 4, marginBottom: 16, fontSize: 12, color: '#2c5282' }}>
            {preset.description}
          </div>
        )}

        {/* Body variables editor */}
        {preset && preset.bodyVarCount > 0 && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 8 }}>
              Variables del cuerpo ({preset.bodyVarCount})
            </label>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>
              Cada variable puede ser un valor fijo o un campo del contacto (se llena dinamicamente por cliente).
            </div>
            {form.template_variables.map((v, i) => (
              <VariableEditor
                key={i}
                label={`{{${i + 1}}} — ${preset.bodyLabels[i] || `Variable ${i + 1}`}`}
                descriptor={v}
                onChange={(d) => updateBodyVar(i, d)}
              />
            ))}
          </div>
        )}

        {/* Header image */}
        {preset && preset.hasHeaderImage && (
          <>
            <label style={{ fontSize: 13, fontWeight: 600 }}>URL de imagen (requerida)</label>
            <input
              style={inputStyle}
              value={form.media_url}
              onChange={(e) => setForm({ ...form, media_url: e.target.value, media_type: 'image' })}
              placeholder="https://johnleopard.com.mx/cdn/shop/files/..."
              required
            />
          </>
        )}

        {/* Button URL variable */}
        {preset && preset.hasDynamicButton && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 8 }}>
              Variable del boton
            </label>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>
              {preset.buttonLabel}
            </div>
            <VariableEditor
              label="Valor del boton"
              descriptor={form.button_url_variable}
              onChange={(d) => setForm({ ...form, button_url_variable: d })}
            />
          </div>
        )}

        <label style={{ fontSize: 13, fontWeight: 600 }}>Lista de contactos</label>
        <select style={inputStyle} value={form.contact_list_id} onChange={(e) => setForm({ ...form, contact_list_id: e.target.value })} required>
          <option value="">Seleccionar lista...</option>
          {lists.map((l) => (
            <option key={l.id} value={l.id}>{l.nombre} ({l.contactCount} contactos)</option>
          ))}
        </select>

        <label style={{ fontSize: 13, fontWeight: 600 }}>Programar envio (opcional) <span style={{ fontWeight: 400, color: '#888' }}>— hora CDMX (UTC-6)</span></label>
        <input type="datetime-local" style={inputStyle} value={form.scheduled_at}
          onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} />

        <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
          <button type="button" onClick={() => navigate('/campaigns')} disabled={sending} style={{
            padding: '10px 24px', border: '1px solid #ddd', borderRadius: 4, background: '#fff', cursor: 'pointer',
            opacity: sending ? 0.5 : 1,
          }}>Cancelar</button>
          {form.scheduled_at ? (
            <button type="submit" disabled={sending} style={{
              padding: '10px 24px', border: 'none', borderRadius: 4, background: '#3498db', color: '#fff', cursor: 'pointer',
              fontWeight: 600,
              opacity: sending ? 0.5 : 1,
            }}>📅 Programar envío</button>
          ) : (
            <button type="submit" disabled={sending} style={{
              padding: '10px 24px', border: 'none', borderRadius: 4, background: '#4ecca3', color: '#fff', cursor: 'pointer',
              opacity: sending ? 0.5 : 1,
            }}>{isEditing ? 'Guardar cambios' : 'Guardar como borrador'}</button>
          )}
          <button type="button" onClick={handleCreateAndSend} disabled={sending} style={{
            padding: '10px 24px', border: 'none', borderRadius: 4, background: '#2ecc71', color: '#fff', cursor: 'pointer',
            fontWeight: 600,
            opacity: sending ? 0.5 : 1,
          }}>{sending ? 'Enviando...' : (isEditing ? '⚡ Guardar y enviar ahora' : '⚡ Crear y enviar ahora')}</button>
        </div>
      </form>
      {preset && (
        <div style={{ width: 340, flexShrink: 0 }}>
          <WhatsAppPreview preset={preset} form={form} />
        </div>
      )}
      </div>
    </div>
  );
}
